import { logger } from '@/core/logger';
import { asyncStorage } from '@/core/storage';
import * as FileSystem from 'expo-file-system';
import { create } from 'zustand';

export type RegistrationType = 'emirates-id' | 'passport';
export type RegistrationStep = 'form' | 'phone-otp' | 'email-otp' | 'document-capture' | 'document-review';

// Registration type constants
export const REGISTRATION_TYPE = {
    EMIRATES_ID: 'emirates-id' as const,
    PASSPORT: 'passport' as const,
} as const;

// Registration step constants
export const REGISTRATION_STEP = {
    FORM: 'form' as const,
    PHONE_OTP: 'phone-otp' as const,
    EMAIL_OTP: 'email-otp' as const,
    DOCUMENT_CAPTURE: 'document-capture' as const,
    DOCUMENT_REVIEW: 'document-review' as const,
} as const;

export interface RegistrationFormData {
    firstName: string;
    middleName: string;
    lastName: string;
    emiratesId?: string; // Only for UAE flow
    passportNumber: string;
    nationality: string; // Nationality value (e.g., "Emirati", "Indian")
    phone?: string; // Phone number (saved after phone OTP verification)
    email?: string; // Email (saved after email OTP verification)
    country_code?: string; // Country code for phone (saved after phone OTP verification)
    tnc_version?: string; // Terms and Conditions version (saved when TNC is accepted, as string)
}

export interface RegistrationDocument {
    doc_type: string;
    doc_id: string;
}

export interface RegistrationResponse {
    customer_id: string;
    registration_id: string;
    documents: RegistrationDocument[];
}

type ManualRegistrationState = {
    registrationType: RegistrationType | null;
    step: RegistrationStep | null;
    formData: RegistrationFormData | null;
    registrationResponse: RegistrationResponse | null;
    phoneOtpVerified: boolean; // Track if phone OTP was verified (for routing)
    emailOtpVerified: boolean; // Track if email OTP was verified (for routing)
    phoneOtpTransactionId: string | null; // Phone OTP transaction ID
    emailOtpTransactionId: string | null; // Email OTP transaction ID
    setRegistrationData: (payload: {
        registrationType: RegistrationType;
        step: RegistrationStep;
        formData: RegistrationFormData;
    }) => void;
    setRegistrationResponse: (response: RegistrationResponse) => void;
    setStep: (step: RegistrationStep) => void;
    setPhoneOtpVerified: (verified: boolean) => void;
    setEmailOtpVerified: (verified: boolean) => void;
    setPhoneOtpTransactionId: (transactionId: string | null) => void;
    setEmailOtpTransactionId: (transactionId: string | null) => void;
    setVerifiedContact: (type: 'phone' | 'email', value: string, countryCode?: string) => void; // Save verified phone/email
    clear: () => void;
    clearDocumentImages: () => Promise<void>;
    clearUploadStatus: () => Promise<void>;
    clearAll: () => Promise<void>; // Clears everything including documents and upload status
};

const STORAGE_KEY = 'manual.registration.state';

// Helper function to serialize current state for persistence
const serializeState = (state: ManualRegistrationState): string => {
    return JSON.stringify({
        registrationType: state.registrationType,
        step: state.step,
        formData: state.formData,
        registrationResponse: state.registrationResponse,
        phoneOtpVerified: state.phoneOtpVerified,
        emailOtpVerified: state.emailOtpVerified,
        phoneOtpTransactionId: state.phoneOtpTransactionId,
        emailOtpTransactionId: state.emailOtpTransactionId,
    });
};

export const useManualRegistrationStore = create<ManualRegistrationState>((set, get) => ({
    registrationType: null,
    step: null,
    formData: null,
    registrationResponse: null,
    phoneOtpVerified: false,
    emailOtpVerified: false,
    phoneOtpTransactionId: null,
    emailOtpTransactionId: null,
    setRegistrationData: ({ registrationType, step, formData }) => {
        set({ registrationType, step, formData });
        void asyncStorage.setItem(STORAGE_KEY, serializeState(get()));
    },
    setRegistrationResponse: (registrationResponse) => {
        set({
            registrationResponse,
            step: REGISTRATION_STEP.DOCUMENT_CAPTURE, // Update step to document-capture after successful registration
        });
        void asyncStorage.setItem(STORAGE_KEY, serializeState(get()));
    },
    setStep: (step) => {
        set({ step });
        void asyncStorage.setItem(STORAGE_KEY, serializeState(get()));
        logger.info('ManualRegistrationStore', 'Step updated', { step });
    },
    setPhoneOtpVerified: (verified) => {
        set({ phoneOtpVerified: verified });
        void asyncStorage.setItem(STORAGE_KEY, serializeState(get()));
        logger.info('ManualRegistrationStore', 'Phone OTP verification status updated', { verified });
    },
    setEmailOtpVerified: (verified) => {
        set({ emailOtpVerified: verified });
        void asyncStorage.setItem(STORAGE_KEY, serializeState(get()));
        logger.info('ManualRegistrationStore', 'Email OTP verification status updated', { verified });
    },
    setPhoneOtpTransactionId: (transactionId) => {
        set({ phoneOtpTransactionId: transactionId });
        void asyncStorage.setItem(STORAGE_KEY, serializeState(get()));
        logger.info('ManualRegistrationStore', 'Phone OTP transaction ID updated', { hasTransactionId: !!transactionId });
    },
    setEmailOtpTransactionId: (transactionId) => {
        set({ emailOtpTransactionId: transactionId });
        void asyncStorage.setItem(STORAGE_KEY, serializeState(get()));
        logger.info('ManualRegistrationStore', 'Email OTP transaction ID updated', { hasTransactionId: !!transactionId });
    },
    setVerifiedContact: (type, value, countryCode) => {
        const currentState = get();
        const updatedFormData = currentState.formData
            ? {
                  ...currentState.formData,
                  [type]: value,
                  ...(type === 'phone' && countryCode ? { country_code: countryCode } : {}),
              }
            : null;
        set({ formData: updatedFormData });
        void asyncStorage.setItem(STORAGE_KEY, serializeState(get()));
        logger.info('ManualRegistrationStore', 'Verified contact saved', { type, hasValue: !!value, countryCode });
    },
    clear: () => {
        set({
            registrationType: null,
            step: null,
            formData: null,
            registrationResponse: null,
            phoneOtpVerified: false,
            emailOtpVerified: false,
            phoneOtpTransactionId: null,
            emailOtpTransactionId: null,
        });
        void asyncStorage.removeItem(STORAGE_KEY);
        logger.info('ManualRegistrationStore', 'Registration state cleared');
    },
    clearDocumentImages: async () => {
        try {
            const documentImagesKey = 'document.images';
            const storedImages = await asyncStorage.getItem(documentImagesKey);
            if (storedImages) {
                try {
                    const images = JSON.parse(storedImages) as Record<string, string>;
                    // Delete actual image files from file system
                    for (const [key, uri] of Object.entries(images)) {
                        const cacheDir = (FileSystem as any).cacheDirectory as string | undefined;
                        if (uri && (uri.startsWith('file://') || (cacheDir && uri.startsWith(cacheDir)))) {
                            try {
                                const fileInfo = await FileSystem.getInfoAsync(uri);
                                if (fileInfo.exists) {
                                    await FileSystem.deleteAsync(uri, { idempotent: true });
                                    logger.debug('ManualRegistrationStore', `Deleted image file: ${key}`, { uri });
                                }
                            } catch (error) {
                                logger.warn('ManualRegistrationStore', `Failed to delete image file: ${key}`, error);
                            }
                        }
                    }
                } catch (error) {
                    logger.warn('ManualRegistrationStore', 'Failed to parse stored images', error);
                }
                await asyncStorage.removeItem(documentImagesKey);
                logger.info('ManualRegistrationStore', 'Cleared document images from storage');
            }
        } catch (error) {
            logger.error('ManualRegistrationStore', 'Failed to clear document images', error);
            throw error;
        }
    },
    clearUploadStatus: async () => {
        try {
            const uploadStatusKey = 'document.upload.status';
            await asyncStorage.removeItem(uploadStatusKey);
            logger.info('ManualRegistrationStore', 'Cleared upload status');
        } catch (error) {
            logger.error('ManualRegistrationStore', 'Failed to clear upload status', error);
            throw error;
        }
    },
    clearAll: async () => {
        try {
            // Clear registration state
            get().clear();
            // Clear document images and files
            await get().clearDocumentImages();
            // Clear upload status
            await get().clearUploadStatus();
            logger.info('ManualRegistrationStore', 'All registration data cleared (state, images, upload status)');
        } catch (error) {
            logger.error('ManualRegistrationStore', 'Failed to clear all registration data', error);
            throw error;
        }
    },
}));

// Rehydrate on app start
void (async () => {
    try {
        const saved = await asyncStorage.getItem(STORAGE_KEY);
        if (saved) {
            const parsed = JSON.parse(saved);
            // Ensure tnc_version is a string if it exists (JSON.parse may convert it to number)
            const formData = parsed?.formData;
            if (formData && formData.tnc_version !== undefined) {
                formData.tnc_version = String(formData.tnc_version);
            }
            useManualRegistrationStore.setState({
                registrationType: parsed?.registrationType ?? null,
                step: parsed?.step ?? null,
                formData: formData ?? null,
                registrationResponse: parsed?.registrationResponse ?? null,
                phoneOtpVerified: parsed?.phoneOtpVerified ?? false,
                emailOtpVerified: parsed?.emailOtpVerified ?? false,
                phoneOtpTransactionId: parsed?.phoneOtpTransactionId ?? null,
                emailOtpTransactionId: parsed?.emailOtpTransactionId ?? null,
            });
            logger.info('ManualRegistrationStore', 'Rehydrated registration state', {
                hasType: !!parsed?.registrationType,
                hasFormData: !!parsed?.formData,
                hasResponse: !!parsed?.registrationResponse,
            });
        }
    } catch (error) {
        logger.warn('ManualRegistrationStore', 'Failed to rehydrate registration state', error);
    }
})();

